/*
 * useModifierKeys
 * Hook para trackear estado de teclas modificadoras (Ctrl, Alt, Shift, Meta)
 * Maneja blur, visibilitychange y limpieza segura de estados
 */

import { useState, useEffect, useCallback } from 'react';

export interface ModifierState {
  ctrl: boolean;
  alt: boolean;
  shift: boolean;
  meta: boolean;
}

export const useModifierKeys = () => {
  const [modifiers, setModifiers] = useState<ModifierState>({
    ctrl: false,
    alt: false,
    shift: false,
    meta: false,
  });

  const clearModifiers = useCallback(() => {
    setModifiers({ ctrl: false, alt: false, shift: false, meta: false });
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      setModifiers({
        ctrl: e.ctrlKey,
        alt: e.altKey,
        shift: e.shiftKey,
        meta: e.metaKey,
      });
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      setModifiers({
        ctrl: e.ctrlKey,
        alt: e.altKey,
        shift: e.shiftKey,
        meta: e.metaKey,
      });
    };

    // Limpiar estados al perder foco o cambiar visibilidad
    const handleBlur = () => {
      clearModifiers();
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        clearModifiers();
      }
    };

    window.addEventListener('keydown', handleKeyDown, true);
    window.addEventListener('keyup', handleKeyUp, true);
    window.addEventListener('blur', handleBlur);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('keydown', handleKeyDown, true);
      window.removeEventListener('keyup', handleKeyUp, true);
      window.removeEventListener('blur', handleBlur);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [clearModifiers]);

  return { modifiers, clearModifiers };
};
